import api from './api';
import { ApiResponse, unwrapApiResponse } from '../utils/api-response.util';
import {
  MedicationResponseDto,
  CreateMedicationDto,
  UpdateMedicationDto,
  MergeMedicationDto,
  MergeMedicationResponseDto,
  GetMedicationsParams,
  PaginatedMedicationsResponseDto,
  Locale,
  MedicationSuggestion,
  MedicationForm
} from './medication.service';
import { ImportResult, ImportSession } from '../types/import.types';

export interface MedicationOptionsResponseDto {
  forms: { value: string; label: string }[];
  experts: { id: number; name: string }[];
  sourceTypes: { value: string; label: string }[];
}

// Interface for the edit response which includes nameI18n
export interface MedicationEditResponseDto extends MedicationResponseDto {
  nameI18n: { [key in Locale]?: string };
}

// Interface for the detailed response
export interface MedicationDetailDto {
  id: number;
  nameI18n: { [key in Locale]?: string };
  detailsI18n?: { [key in Locale]?: string }; // Added detailsI18n
  form: MedicationForm;
  dosage: string[] | null;

  isStructured: boolean;
  isSystem: boolean; // Added isSystem
  imageId: number | null;
  imageUrl: string | null;
  externalLink: string | null;

  createdByUserId: number | null;
  creator: {
    id: number;
    fullName: string;
    email: string;
  } | null;

  usageCount: number;
  createdAt: string;
  updatedAt: string;

  legacyId?: string | null; // Added legacyId
  legacyEntity?: 'preparat' | null; // Added legacyEntity
  legacyImg?: string | null; // Added legacyImg
}

export type ImportMedicationsResponseDto = ImportResult;
export type ImportSessionDto = ImportSession;

class AdminMedicationService {
  private readonly BASE_URL = '/admin/lib/medications';
  private readonly IMPORT_BASE_URL = '/admin/lib/import';

  async getMedications(params: GetMedicationsParams): Promise<PaginatedMedicationsResponseDto> {
    const response = await api.get<any>(`${this.BASE_URL}`, {
      params,
    });

    const body = response.data;
    if (body && body.success && Array.isArray(body.data)) {
      return {
        data: body.data,
        meta: body.meta,
      };
    }
    
    // Fallback for different response structure
    if (body && Array.isArray(body.data)) {
       return {
        data: body.data,
        meta: body.meta || { total: body.data.length, page: 1, limit: params.limit || 10 },
      };
    }

    console.error('Unexpected API response structure for admin getMedications:', body);
    return { 
      data: [], 
      meta: { total: 0, page: 1, limit: params.limit || 10 } 
    };
  }

  async getMedicationById(id: number): Promise<MedicationResponseDto> {
    const response = await api.get<ApiResponse<MedicationResponseDto>>(`${this.BASE_URL}/${id}`);
    return unwrapApiResponse<MedicationResponseDto>(response);
  }

  async getMedicationForEdit(id: number): Promise<MedicationEditResponseDto> {
    const response = await api.get<ApiResponse<MedicationEditResponseDto>>(`${this.BASE_URL}/${id}/edit`);
    return unwrapApiResponse<MedicationEditResponseDto>(response);
  }

  async getMedicationDetail(id: number): Promise<MedicationDetailDto> {
    const response = await api.get<ApiResponse<MedicationDetailDto>>(`${this.BASE_URL}/${id}/detail`);
    return unwrapApiResponse<MedicationDetailDto>(response);
  }

  async createMedication(data: CreateMedicationDto): Promise<MedicationResponseDto> {
    const response = await api.post<ApiResponse<MedicationResponseDto>>(`${this.BASE_URL}`, data);
    return unwrapApiResponse<MedicationResponseDto>(response);
  }

  async updateMedication(id: number, data: UpdateMedicationDto): Promise<MedicationResponseDto> {
    const response = await api.patch<ApiResponse<MedicationResponseDto>>(`${this.BASE_URL}/${id}`, data);
    return unwrapApiResponse<MedicationResponseDto>(response);
  }

  async deleteMedication(id: number): Promise<{ message: string }> {
    const response = await api.delete<{ message: string }>(`${this.BASE_URL}/${id}`);
    return response.data;
  }

  async mergeMedications(data: MergeMedicationDto): Promise<MergeMedicationResponseDto> {
    const response = await api.post<ApiResponse<MergeMedicationResponseDto>>(`${this.BASE_URL}/merge`, data);
    return unwrapApiResponse<MergeMedicationResponseDto>(response);
  }
  
  async getMedicationSuggestions(query: string, limit: number = 10): Promise<MedicationSuggestion[]> {
      const params: GetMedicationsParams = {
        search: query,
        limit: limit,
      };
      const response = await this.getMedications(params);
  
      if (!response || !Array.isArray(response.data)) {
        return [];
      }
      
      return response.data.map(med => ({
        id: med.id,
        name: med.name,
        form: med.form,
        dosage: med.dosage || [],
        isStructured: med.isStructured,
        isSystem: med.isSystem,
      }));
    }

  // Options
  async getMedicationOptions(): Promise<MedicationOptionsResponseDto> {
    const response = await api.get<ApiResponse<MedicationOptionsResponseDto>>(`${this.BASE_URL}/options`);
    return unwrapApiResponse<MedicationOptionsResponseDto>(response);
  }

  // Import functionality
  async downloadImportTemplate(): Promise<Blob> {
    const response = await api.get(`${this.IMPORT_BASE_URL}/medications/template`, {
      responseType: 'blob',
    });
    return response.data;
  }

  async importMedications(file: File): Promise<ImportResult> {
    const formData = new FormData();
    formData.append('file', file);

    const response = await api.post<ApiResponse<ImportResult>>(`${this.IMPORT_BASE_URL}/medications`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    
    return unwrapApiResponse<ImportResult>(response);
  }

  async getImportHistory(): Promise<ImportSession[]> {
    const response = await api.get<ApiResponse<ImportSession[]>>(`${this.IMPORT_BASE_URL}/history`);
    return unwrapApiResponse<ImportSession[]>(response);
  }

  async revertImport(sessionId: number): Promise<void> {
    await api.delete<ApiResponse<void>>(`${this.IMPORT_BASE_URL}/${sessionId}`);
  }
}

export const adminMedicationService = new AdminMedicationService();
